import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { ReceiptField } from "../components/ReceiptField";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { parseMoneyToCents } from "../lib/format";
import { toast } from "sonner";

export default function AddExpense() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const editing = !!id && id !== "new";

  const { data: categories } = useQuery({
    queryKey: ["expense-categories"],
    queryFn: async () => (await api.get(`/expenses/categories`)).data,
  });
  const { data: practices } = useQuery({
    queryKey: ["practices-min"],
    queryFn: async () => (await api.get(`/practices`)).data,
  });
  const { data: existing } = useQuery({
    queryKey: ["expense", id],
    queryFn: async () => (await api.get(`/expenses/${id}`)).data,
    enabled: editing,
  });

  const [form, setForm] = useState({
    date: new Date().toLocaleDateString("en-CA"), practice_id: "", category: "", description: "", amount: "",
    gst_included: false, bas_1b: "review", receipt_path: null, receipt_name: null, receipt_content_type: null, receipt_file_id: null,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({
        date: existing.date, practice_id: existing.practice_id || "", category: existing.category,
        description: existing.description || "", amount: (existing.amount_cents / 100).toFixed(2),
        gst_included: !!existing.gst_included, bas_1b: existing.bas_1b || "review",
        receipt_path: existing.receipt_path || null, receipt_name: existing.receipt_name || null,
        receipt_content_type: existing.receipt_content_type || null, receipt_file_id: null,
      });
    }
  }, [existing]);

  const setReceipt = (r) => setForm((f) => ({
    ...f,
    receipt_path: r?.receipt_path || null, receipt_name: r?.receipt_name || null,
    receipt_content_type: r?.receipt_content_type || null, receipt_file_id: r?.receipt_file_id || null,
  }));

  const save = async () => {
    if (!form.category) return toast.error("Please choose a category.");
    if (parseMoneyToCents(form.amount) <= 0) return toast.error("Please enter an amount.");
    setSaving(true);
    const payload = {
      date: form.date, practice_id: form.practice_id || null, category: form.category,
      description: form.description || null, amount_cents: parseMoneyToCents(form.amount),
      gst_included: form.gst_included, bas_1b: form.gst_included ? form.bas_1b : null,
      receipt_path: form.receipt_path, receipt_name: form.receipt_name, receipt_content_type: form.receipt_content_type,
    };
    try {
      if (editing) await api.put(`/expenses/${id}`, payload);
      else await api.post("/expenses", payload);
      qc.invalidateQueries();
      toast.success(editing ? "Expense updated" : "Expense saved");
      navigate("/money/expenses");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <AppShell>
      <PageHeader title={editing ? "Edit Expense" : "Add Expense"} onBack={() => navigate(-1)} />
      <div className="px-5 lg:px-8 pb-40 df-fade-in space-y-4">
        <div className="df-card p-4 space-y-4">
          <div>
            <Label className="text-slate-700">Date</Label>
            <Input type="date" data-testid="expense-date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <div>
            <Label className="text-slate-700">Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="expense-category" className="mt-1.5 h-12 rounded-xl bg-white"><SelectValue placeholder="Choose a category" /></SelectTrigger>
              <SelectContent>{(categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700">Amount</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
              <Input type="number" step="0.01" inputMode="decimal" data-testid="expense-amount" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" className="h-12 rounded-xl bg-white pl-7 font-num" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-slate-700">GST included</Label>
            <Switch data-testid="expense-gst" checked={form.gst_included} onCheckedChange={(v) => set("gst_included", v)} />
          </div>
          {form.gst_included && (
            <div className="mt-3" data-testid="expense-bas1b-wrap">
              <Label className="text-slate-700 text-sm">Include this GST in BAS purchases (1B)?</Label>
              <div className="flex gap-2 mt-1.5">
                {[["yes", "Yes"], ["no", "No"], ["review", "Review"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`expense-bas1b-${v}`} onClick={() => set("bas_1b", v)}
                    className={`flex-1 h-10 rounded-xl border text-sm font-medium df-pressable ${form.bas_1b === v ? "bg-[#0D786A] text-white border-[#0D786A]" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">Use the treatment that applies to this purchase. Check with your accountant or tax adviser if unsure.</p>
            </div>
          )}
          <div>
            <Label className="text-slate-700">Practice (optional)</Label>
            <Select value={form.practice_id} onValueChange={(v) => set("practice_id", v)}>
              <SelectTrigger data-testid="expense-practice" className="mt-1.5 h-12 rounded-xl bg-white"><SelectValue placeholder="Not practice-specific" /></SelectTrigger>
              <SelectContent>{(practices || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700">Description (optional)</Label>
            <Textarea data-testid="expense-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-xl bg-white" />
          </div>
          <div>
            <Label className="text-slate-700">Receipt (optional)</Label>
            <ReceiptField
              value={{ receipt_path: form.receipt_path, receipt_name: form.receipt_name, receipt_content_type: form.receipt_content_type, receipt_file_id: form.receipt_file_id }}
              onChange={setReceipt}
              expenseId={editing ? id : null}
            />
          </div>
        </div>
      </div>
      <div className="df-actionbar">
        <Button variant="outline" onClick={() => navigate(-1)} className="flex-1 h-12 rounded-xl">Cancel</Button>
        <Button data-testid="expense-save" onClick={save} disabled={saving} className="flex-1 h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
          {saving ? "Saving…" : editing ? "Save Changes" : "Save Expense"}
        </Button>
      </div>
    </AppShell>
  );
}
